import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { fetchInvoiceDetail, type InvoiceDetail } from '../../lib/invoices';
import { useOrderDetailDrawerStore } from '../../stores/order-detail-drawer-store';
import {
  computeInvoiceStatus,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
} from '../../lib/invoice-status';
import { BRAND_COLORS, SF_BASE_URL } from '../../lib/order-constants';
import { formatCurrency, formatDate } from '../../lib/format';
import { InvoiceLineItems } from './InvoiceLineItems';
import { EditInvoiceAddressDialog } from './EditInvoiceAddressDialog';
import { InvoiceInternalNote } from './InvoiceInternalNote';
import { SendInvoiceButton } from './SendInvoiceButton';
import { InvoicePdfButton } from './InvoicePdfButton';
import { CreditInvoiceDialog } from './CreditInvoiceDialog';
import { CreditfacturenSection } from './CreditfacturenSection';
import { TransactiesSection } from '../transacties/TransactiesSection';
import { RefundsSection } from '../order-detail/RefundsSection';

type Variant = 'page' | 'drawer';

interface InvoiceDetailViewProps {
  invoiceId: string;
  variant: Variant;
  onClose?: () => void;
}

function ExternalLinkIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

function renderAddress(formatted: string) {
  if (!formatted) return null;
  // Splits op zowel <br> (Account-formula) als \n (override-input) zodat
  // beide bronnen visueel identiek renderen.
  const lines = formatted
    .split(/<br\s*\/?>(?:\s*)?|\r?\n/i)
    .map((s) => s.trim())
    .filter(Boolean);
  return lines.map((line, i) => (
    <span key={i} className="block">
      {line}
    </span>
  ));
}

function MollieCard({
  paymentUrl,
  paymentId,
  paymentStatus,
}: {
  paymentUrl: string;
  paymentId: string;
  paymentStatus: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no clipboard */
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Mollie betaallink
        </h2>
        {paymentStatus && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700">
            {paymentStatus}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <a
          href={paymentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-xs text-indigo-600 hover:underline truncate"
        >
          {paymentUrl}
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-slate-500 hover:text-slate-700 flex-shrink-0"
        >
          {copied ? 'Gekopieerd' : 'Kopieer'}
        </button>
      </div>
      {paymentId && (
        <p className="mt-1 text-[11px] text-slate-400 font-mono">ID: {paymentId}</p>
      )}
    </div>
  );
}

export function InvoiceDetailView({ invoiceId, variant, onClose }: InvoiceDetailViewProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [refundsRefreshKey, setRefundsRefreshKey] = useState(0);
  const openOrderDrawer = useOrderDetailDrawerStore((s) => s.open);

  const refetch = useCallback(() => {
    fetchInvoiceDetail(invoiceId)
      .then(setData)
      .catch(() => {
        /* stil */
      });
  }, [invoiceId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchInvoiceDetail(invoiceId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (err.status === 404) {
          setNotFound(true);
        } else {
          setError(err.message || 'Kon factuur niet laden');
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const isDrawer = variant === 'drawer';

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Factuur laden...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
        <p className="text-sm">Factuur niet gevonden</p>
        <button
          onClick={() => (isDrawer && onClose ? onClose() : navigate(-1))}
          className="text-indigo-600 text-sm hover:underline"
        >
          {isDrawer ? 'Sluiten' : 'Terug'}
        </button>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error || 'Onbekende fout'}
        </div>
        <button
          onClick={() => (isDrawer && onClose ? onClose() : navigate(-1))}
          className="text-indigo-600 text-sm hover:underline"
        >
          {isDrawer ? 'Sluiten' : 'Terug'}
        </button>
      </div>
    );
  }

  const { invoice, lineItems, transacties } = data;
  const status = computeInvoiceStatus({
    totalInclBtw: invoice.totals.totalInclBtw,
    openAmount: invoice.status.openAmount,
    transactionTotal: invoice.status.transactionTotal,
    paid: invoice.status.paid,
  });
  const statusLabel = INVOICE_STATUS_LABELS[status];
  const statusColor = INVOICE_STATUS_COLORS[status];
  const showMollie = invoice.status.openAmount > 0 && !!invoice.mollie.paymentUrl;
  const isCredit = invoice.credit.isCreditfactuur;
  const titlePrefix = isCredit ? 'Creditfactuur' : 'Factuur';
  const alreadyCredited = invoice.credit.creditInvoices.reduce(
    (sum, c) => sum + Math.abs(c.totalInclBtw),
    0
  );
  const fullyCredited = alreadyCredited >= invoice.totals.totalInclBtw - 0.01;
  const canCredit = !isCredit && !fullyCredited && invoice.totals.totalInclBtw > 0;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Drawer sticky header */}
      {isDrawer && (
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-slate-900 truncate">
            {titlePrefix} {invoice.name}
          </h2>
          {isCredit && (
            <span className="text-xs px-2 py-0.5 rounded font-medium bg-purple-100 text-purple-700 flex-shrink-0">
              Credit
            </span>
          )}
          {invoice.workOrderBrand && (
            <span
              className={clsx(
                'text-xs px-2 py-0.5 rounded font-medium flex-shrink-0',
                BRAND_COLORS[invoice.workOrderBrand] || 'bg-slate-100 text-slate-600'
              )}
            >
              {invoice.workOrderBrand === 'SCHOONHUISSHOP' ? 'SHS' : invoice.workOrderBrand}
            </span>
          )}
          <span
            className={clsx('text-xs px-2 py-0.5 rounded font-medium flex-shrink-0', statusColor)}
          >
            {statusLabel}
          </span>
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            <InvoicePdfButton invoiceId={invoice.id} invoiceName={invoice.name} compact />
            <SendInvoiceButton
              invoiceId={invoice.id}
              invoiceName={invoice.name}
              recipientEmail={invoice.email}
              compact
            />
            <Link
              to={`/facturen/${invoice.id}`}
              onClick={onClose}
              className="text-xs text-indigo-600 hover:underline"
            >
              Open volledig →
            </Link>
          </div>
        </div>
      )}

      {/* Page header */}
      {!isDrawer && (
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug
          </button>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-xl font-semibold text-slate-900">
                  {titlePrefix} {invoice.name}
                </h1>
                {isCredit && (
                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-purple-100 text-purple-700">
                    Creditfactuur
                    {invoice.credit.creditedInvoiceName
                      ? ` bij ${invoice.credit.creditedInvoiceName}`
                      : ''}
                  </span>
                )}
                <span className={clsx('text-xs px-2 py-0.5 rounded font-medium', statusColor)}>
                  {statusLabel}
                </span>
                {invoice.workOrderBrand && (
                  <span
                    className={clsx(
                      'text-xs px-2 py-0.5 rounded font-medium',
                      BRAND_COLORS[invoice.workOrderBrand] || 'bg-slate-100 text-slate-600'
                    )}
                  >
                    {invoice.workOrderBrand === 'SCHOONHUISSHOP' ? 'SHS' : invoice.workOrderBrand}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <a
                  href={`${SF_BASE_URL}/${invoice.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                >
                  <ExternalLinkIcon />
                  Open in Salesforce
                </a>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <InvoicePdfButton invoiceId={invoice.id} invoiceName={invoice.name} />
              {canCredit && (
                <button
                  type="button"
                  onClick={() => setCreditDialogOpen(true)}
                  className="text-sm font-medium px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Crediteren…
                </button>
              )}
              <SendInvoiceButton
                invoiceId={invoice.id}
                invoiceName={invoice.name}
                recipientEmail={invoice.email}
              />
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className={clsx('flex-1 overflow-y-auto space-y-6', isDrawer ? 'p-4' : 'p-6')}>
        {/* Header-card: meta */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="grid gap-3 md:grid-cols-3 text-sm">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Factuurdatum
              </p>
              <p className="text-slate-900">{formatDate(invoice.invoiceDate) || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Vervaldatum
              </p>
              <p className={clsx(
                status === 'open' && invoice.dueDate && new Date(invoice.dueDate) < new Date()
                  ? 'text-amber-600'
                  : 'text-slate-900'
              )}>
                {formatDate(invoice.dueDate) || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Werk-order
              </p>
              {invoice.workOrderId ? (
                <button
                  type="button"
                  onClick={() => openOrderDrawer(invoice.workOrderId)}
                  className="text-indigo-600 hover:underline"
                >
                  {invoice.workOrderRef || invoice.workOrderId}
                </button>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>
          {isDrawer && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs">
              <a
                href={`${SF_BASE_URL}/${invoice.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
              >
                <ExternalLinkIcon />
                Salesforce
              </a>
              {invoice.accountId && (
                <Link
                  to={`/klanten/${invoice.accountId}?type=account`}
                  onClick={onClose}
                  className="text-indigo-600 hover:underline"
                >
                  {invoice.accountName || 'Klant'}
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Bedrag-card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                {status === 'paid' ? 'Voldaan' : status === 'partial' ? 'Nog openstaand' : 'Openstaand'}
              </p>
              <div className="text-3xl font-semibold text-slate-900">
                {status === 'paid'
                  ? formatCurrency(invoice.totals.totalInclBtw)
                  : formatCurrency(invoice.status.openAmount)}
              </div>
              {status === 'partial' && (
                <p className="text-xs text-slate-500 mt-1">
                  Al betaald: {formatCurrency(invoice.status.transactionTotal)} van{' '}
                  {formatCurrency(invoice.totals.totalInclBtw)}
                </p>
              )}
              {status === 'open' && invoice.dueDate && (
                <p className="text-xs text-slate-500 mt-1">
                  Vervaldatum: {formatDate(invoice.dueDate)}
                </p>
              )}
            </div>
            <span className={clsx('text-xs px-2.5 py-1 rounded font-medium', statusColor)}>
              {status === 'paid' && '✓ '}
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Credit-relaties: lijst credits op origineel, link naar origineel op credit */}
        <CreditfacturenSection credit={invoice.credit} onNavigate={isDrawer ? onClose : undefined} />

        {/* Adres-card — altijd tonen zodat "Toevoegen"-flow werkt */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Factuuradres
            </h2>
            <button
              type="button"
              onClick={() => setEditAddressOpen(true)}
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
              aria-label="Factuurgegevens bewerken"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
              </svg>
              Bewerken
            </button>
          </div>
          {invoice.address.nameOnInvoice || invoice.address.formatted || invoice.address.tav ? (
            <div className="text-sm text-slate-900 leading-snug">
              {invoice.address.nameOnInvoice && (
                <span className="flex items-center gap-1.5 font-medium">
                  {invoice.address.nameOnInvoice}
                  {invoice.address.nameOverride && (
                    <span
                      title="Override op factuurniveau — wijkt af van werkorder/klant"
                      className="text-[10px] px-1.5 py-0.5 rounded font-normal bg-blue-100 text-blue-700"
                    >
                      ✱ Aangepast
                    </span>
                  )}
                </span>
              )}
              {invoice.address.tav && (
                <span className="block text-slate-500">t.a.v. {invoice.address.tav}</span>
              )}
              {renderAddress(invoice.address.formatted)}
              {invoice.address.formattedOverride && (
                <span
                  title="Override op factuurniveau — wijkt af van klant-billing-adres"
                  className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-normal bg-blue-100 text-blue-700"
                >
                  ✱ Aangepast
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Nog geen adres ingevuld</p>
          )}
        </div>

        {/* Mollie */}
        {showMollie && (
          <MollieCard
            paymentUrl={invoice.mollie.paymentUrl}
            paymentId={invoice.mollie.paymentId}
            paymentStatus={invoice.mollie.paymentStatus}
          />
        )}

        {/* Interne opvolging — alleen kantoor, los van het klantgerichte veld */}
        <InvoiceInternalNote
          invoiceId={invoice.id}
          internalNote={invoice.internalNote}
          promisedPaymentDate={invoice.promisedPaymentDate}
          openAmount={invoice.status.openAmount}
          onSaved={refetch}
        />

        {/* Opmerkingen op factuur — LET OP: dit verschijnt op de factuur naar de klant */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Opmerkingen op factuur
            </h2>
            <button
              type="button"
              onClick={() => setEditAddressOpen(true)}
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
              aria-label="Opmerkingen bewerken"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
              </svg>
              Bewerken
            </button>
          </div>
          {invoice.notes ? (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{invoice.notes}</p>
          ) : (
            <p className="text-sm text-slate-400 italic">Geen opmerkingen</p>
          )}
        </div>

        {/* Line items */}
        <InvoiceLineItems items={lineItems} totals={invoice.totals} />

        {/* Transacties */}
        <TransactiesSection
          parentType="Invoice__c"
          parentId={invoice.id}
          transacties={transacties}
          openAmount={invoice.status.openAmount}
          onMutated={refetch}
        />

        {/* Refunds van de gekoppelde werkorder — volledige geldgeschiedenis */}
        {invoice.workOrderId && (
          <RefundsSection werkOrderId={invoice.workOrderId} refreshKey={refundsRefreshKey} />
        )}

        {/* System info */}
        <div className="text-xs text-slate-400 flex items-center gap-4 flex-wrap">
          <span>Aangemaakt op {formatDate(invoice.createdDate)}</span>
          {invoice.payment.term && <span>Termijn: {invoice.payment.term}</span>}
        </div>
      </div>

      <EditInvoiceAddressDialog
        isOpen={editAddressOpen}
        invoice={invoice}
        onClose={() => setEditAddressOpen(false)}
        onSaved={refetch}
      />

      {creditDialogOpen && (
        <CreditInvoiceDialog
          isOpen={creditDialogOpen}
          invoice={invoice}
          lineItems={lineItems}
          onClose={() => setCreditDialogOpen(false)}
          onSuccess={() => {
            setRefundsRefreshKey((k) => k + 1);
            refetch();
          }}
        />
      )}
    </div>
  );
}
